import dayjs from 'dayjs';

const REPORT_DATE_TIME_FORMAT = 'YYYY-MM-DDTHH:mm';
const FULL_DAY_START = '00:00';
const FULL_DAY_END = '23:59';

const normalizeTime = (value, fallback) => {
  const match = String(value || '').trim().match(/^([01]\d|2[0-3]):([0-5]\d)/);
  return match ? `${match[1]}:${match[2]}` : fallback;
};

const normalizeSchedule = (rows) => (Array.isArray(rows) ? rows : [])
  .map((row) => ({
    day_of_week: Number(row?.day_of_week ?? row?.dayOfWeek ?? row?.day),
    shift_start: normalizeTime(row?.shift_start ?? row?.shiftStart, FULL_DAY_START),
    shift_end: normalizeTime(row?.shift_end ?? row?.shiftEnd, FULL_DAY_END),
  }))
  .filter((row) => Number.isInteger(row.day_of_week) && row.day_of_week >= 0 && row.day_of_week <= 6);

const withTime = (value, time) => {
  const [hour, minute] = time.split(':').map(Number);
  return dayjs(value).hour(hour).minute(minute).second(0).millisecond(0);
};

export const getReportBoundaryTime = (rows, boundary, dateValue) => {
  const schedule = normalizeSchedule(rows);
  const isEnd = boundary === 'end';
  if (schedule.length === 0) return isEnd ? FULL_DAY_END : FULL_DAY_START;

  const date = dayjs(dateValue);
  const matchingRow = date.isValid()
    ? schedule.find((row) => row.day_of_week === date.day())
    : null;
  if (matchingRow) return isEnd ? matchingRow.shift_end : matchingRow.shift_start;

  // The outer report range may start/end on a non-scheduled day. In that case,
  // use the broadest configured boundary; the backend schedule filter still
  // decides which weekdays and hours are included in the report.
  const times = schedule.map((row) => (isEnd ? row.shift_end : row.shift_start)).sort();
  return isEnd ? times[times.length - 1] : times[0];
};

export const applyReportBoundaryDefault = ({
  nextValue,
  currentValue,
  boundary,
  schedule = [],
}) => {
  const next = dayjs(nextValue);
  if (!next.isValid()) return '';

  const current = dayjs(currentValue);
  const dateChanged = !currentValue || !current.isValid() || !next.isSame(current, 'day');
  const resolved = dateChanged
    ? withTime(next, getReportBoundaryTime(schedule, boundary, next))
    : next.second(0).millisecond(0);

  return resolved.format(REPORT_DATE_TIME_FORMAT);
};

export const buildReportEndForStart = (startValue, schedule = []) => {
  const start = dayjs(startValue);
  if (!start.isValid()) return '';

  let end = withTime(start, getReportBoundaryTime(schedule, 'end', start));
  if (!end.isAfter(start)) {
    end = start.add(1, 'minute');
  }

  return end.format(REPORT_DATE_TIME_FORMAT);
};

export const isReportEndAfterStart = (startValue, endValue) => {
  const start = dayjs(startValue);
  const end = dayjs(endValue);
  return start.isValid() && end.isValid() && end.isAfter(start);
};

export const REPORT_END_INCLUSIVE_MINUTE_FLAG = 'tstop_inclusive_minute';
